import React, {
  ReactNode,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import Button from 'pages/Dashboard/components/Button';
import Spinner from 'pages/Dashboard/components/Spinner';
import ToolTip from 'core/components/Tooltip';
import Skeleton from 'core/components/Skeleton';
import IconButton from '@mui/material/IconButton';
import Name from 'pages/Dashboard/components/Name';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import type {
  PatientAuthorizationResponseDTO,
} from 'dtos';
import {
  formatDateTime,
  formatTwoDigitDate,
} from 'utils/date';
import {
  getErrorMessage,
} from 'utils/errors';
import AuthorizationStatusBadge from 'views/EMR/components/Authorizations/StatusBadge';
import AuthorizationNumber from 'views/EMR/components/Authorizations/AuthorizationNumber';
import SanitizedHtml from 'views/EMR/components/SanitizedHtml';
import {
  notEmptyString,
} from '@populateui/utils';

type AuthorizationDetailsErrorProps = {
  error: unknown;
  onClose?: () => void;
  onRetry: () => void;
};

type AuthorizationDetailsHeaderProps = {
  authorization: PatientAuthorizationResponseDTO;
  header?: ReactNode;
  actions?: ReactNode;
  onClose?: () => void;
};

const panelClassName = 'flex h-full w-[40rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden';

type CloseButtonProps = {
  onClose?: () => void;
};

function CloseButton({ onClose }: CloseButtonProps) {
  return (
    <ToolTip title="Close">
      <IconButton aria-label="Close authorization details" size="small" onClick={onClose}>
        <CloseIcon className="!h-5 !w-5" />
      </IconButton>
    </ToolTip>
  );
}

export function AuthorizationDetailsLoading({ onClose }: CloseButtonProps) {
  return (
    <div className={panelClassName}>
      <div className="flex items-center justify-between border-b p-4">
        <Skeleton variant="text" className="!h-6 !w-48" />
        <CloseButton onClose={onClose} />
      </div>
      <div className="flex flex-1 items-center justify-center">
        <Spinner size={28} />
      </div>
    </div>
  );
}

export function AuthorizationDetailsError({
  error,
  onClose,
  onRetry,
}: AuthorizationDetailsErrorProps) {
  return (
    <div className={panelClassName}>
      <div className="flex items-center justify-between border-b p-4">
        <CloseButton onClose={onClose} />
      </div>
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center">
        <p className="text-sm text-gray-500">
          {getErrorMessage(error) ?? 'Unable to load authorization details.'}
        </p>
        <Button variant="outlined" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
}

export function AuthorizationDetailsHeader({
  authorization,
  header,
  actions,
  onClose,
}: AuthorizationDetailsHeaderProps) {
  const updatedBy = authorization.updatedByUserName?.fullNameWithCredentials
    ?? authorization.updatedByUserName?.fullName
    ?? (authorization.updatedByUserId ? `User ${authorization.updatedByUserId}` : DEFAULT_EMPTY_VALUE);
  const imageSrc = useUserProfilePicturePath(authorization.updatedByUserId ?? undefined);

  return (
    <div className="flex items-start border-b p-4">
      {header && <div className="mr-2">{header}</div>}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <AuthorizationNumber
            authorizationNumber={authorization.authorizationNumber}
            className="text-sm"
          />
          <AuthorizationStatusBadge status={authorization.status} />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-gray-500">
          Updated on
          <span className="font-medium text-gray-800">
            {formatDateTime(authorization.updatedOn)}
          </span>
          by
          <Name
            {...authorization.updatedByUserName}
            name={updatedBy}
            image={{ size: 'xs', src: imageSrc }}
            className="!w-auto !gap-1 font-medium text-gray-800"
          />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {actions}
        <CloseButton onClose={onClose} />
      </div>
    </div>
  );
}

type AuthorizationPayerProps = {
  authorization: PatientAuthorizationResponseDTO;
};

export function AuthorizationPayer({ authorization }: AuthorizationPayerProps) {
  const payerInfo = [authorization.payer.payerId, authorization.payer.insuranceType]
    .filter(Boolean)
    .join(' · ');

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="mb-2 text-sm font-semibold text-gray-600">Insurance</h2>
        <p className="break-words text-xs text-gray-800">
          <span className="font-medium">
            {authorization.payer.payerName}
          </span>
          <span className="ml-2 text-xs text-gray-500">{payerInfo}</span>
        </p>
      </div>
      <div>
        <h2 className="mb-2 text-sm font-semibold text-gray-600">Effective</h2>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <div className="flex gap-1">
            <span className="text-gray-500">From:</span>
            <span className="text-gray-800">{formatTwoDigitDate(authorization.effectiveDate)}</span>
          </div>
          <div className="flex gap-1">
            <span className="text-gray-500">To:</span>
            <span className="text-gray-800">{formatTwoDigitDate(authorization.expirationDate)}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

type AuthorizationNotesProps = {
  notes?: string | null;
};

export function AuthorizationNotes({ notes }: AuthorizationNotesProps) {
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold text-gray-600">Notes</h2>
      {notEmptyString(notes) ? (
        <SanitizedHtml content={notes ?? ''} className="h-full overflow-y-auto text-xs text-gray-800" />
      ) : (
        <p className="text-xs text-gray-800">{DEFAULT_EMPTY_VALUE}</p>
      )}
    </section>
  );
}
